
import { useState, useEffect, useRef } from "react";
import { Challenge } from "../lib/types";
import { getChallenge, voteForSubmission } from "../lib/api";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "../lib/auth";
import { useGuest } from "../lib/useGuest";
import { toast } from "sonner";

// Consolidated challenge + voting logic in a hook
export function usePastChallenges() {
  const [pastChallenges, setPastChallenges] = useState<Challenge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [votedSubmissions, setVotedSubmissions] = useState<string[]>([]);
  const { toast: uiToast } = useToast();
  const { user } = useAuth();
  const { isGuest } = useGuest();
  const lastVoteRef = useRef(Date.now());

  useEffect(() => {
    const fetchResults = async () => {
      setIsLoading(true);
      try {
        // In a real app, we would fetch past challenges from the API
        // For now, we'll use the current challenge
        const currentChallenge = await getChallenge();

        // Create a mock history of past challenges
        const mockPastChallenges: Challenge[] = [
          {
            ...currentChallenge,
            id: "past-1",
            date: "2023-06-01",
            status: "closed" as const,
            selectedPrompt: { id: "prompt-1", text: "Urban Wildlife", votes: 45 },
            winner: currentChallenge.submissions[0] || {
              id: "submission-1",
              userId: "1",
              username: "photogeek",
              photoUrl: "https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388",
              votes: 12,
              timestamp: new Date().toISOString(),
              comments: []
            }
          },
          {
            ...currentChallenge,
            id: "past-2",
            date: "2023-06-02",
            status: "closed" as const,
            selectedPrompt: { id: "prompt-2", text: "Morning Light", votes: 32 },
            winner: currentChallenge.submissions[1] || {
              id: "submission-2",
              userId: "2",
              username: "snapmaster",
              photoUrl: "https://images.unsplash.com/photo-1588392382834-a891154bca4d",
              votes: 9,
              timestamp: new Date().toISOString(),
              comments: []
            }
          }
        ];

        setPastChallenges(mockPastChallenges);

        // Set initially voted submissions based on user's likes
        if (user) {
          const votedIds: string[] = [];
          mockPastChallenges.forEach((challenge) => {
            challenge.submissions.forEach((submission) => {
              if (submission.likedBy?.includes(user.id)) {
                votedIds.push(submission.id);
              }
            });
            if (challenge.winner?.likedBy?.includes(user.id)) {
              votedIds.push(challenge.winner.id);
            }
          });
          setVotedSubmissions(votedIds);
        }
      } catch (error) {
        console.error("Error fetching results:", error);
        uiToast({
          title: "Error",
          description: "Could not load the results. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [user?.id]);

  const handleVote = async (submissionId: string) => {
    if (!user && !isGuest) {
      toast("Please log in or enter as guest to vote");
      return;
    }
    if (isGuest) {
      toast("Create an account to submit photos or comment!");
    }
    setVotedSubmissions((prev) => [...prev, submissionId]);
    lastVoteRef.current = Date.now();

    if (!isGuest) {
      try {
        await voteForSubmission(submissionId, user.id);
        toast("Vote recorded!");
        // Update the challenges with the new vote count
        const updatedChallenge = await getChallenge();
        setPastChallenges((prevChallenges) => {
          return prevChallenges.map((challenge) => {
            // Update winner if it matches the submission ID
            if (challenge.winner?.id === submissionId) {
              const updatedSubmission =
                updatedChallenge.submissions.find((s) => s.id === submissionId) ||
                challenge.winner;
              return { ...challenge, winner: updatedSubmission };
            }

            // Update regular submissions
            const updatedSubmissions = challenge.submissions.map((submission) => {
              if (submission.id === submissionId) {
                const updatedSubmission =
                  updatedChallenge.submissions.find((s) => s.id === submissionId) ||
                  submission;
                return updatedSubmission;
              }
              return submission;
            });

            return { ...challenge, submissions: updatedSubmissions };
          });
        });
      } catch (error) {
        setVotedSubmissions((prev) => prev.filter((id) => id !== submissionId));
        toast("Failed to record vote");
      }
    }
  };

  return {
    pastChallenges,
    isLoading,
    votedSubmissions,
    handleVote,
    isGuest,
    lastVoteRef
  };
}
